import { supabaseClient } from '../supabase'
import baseDataProvider from '../supabase/baseDataProvider'
import { getItemFromLocalStorage } from '../common/LocalStorage'
import { getCachedIdentity } from '../supabase/customAuthProvider'
import { safeUUID } from '../common/isUUID'

export const dataProvider = {
  ...baseDataProvider,
  getList: async (resource, params) => {
    const useView = params?.meta?.useView
    resource = useView === 'paie' ? 'profiles_view_paie' : useView ? 'profiles_view' : 'profiles'

    const localUserGrade = getItemFromLocalStorage('userRoleGradeOverride') ?? getCachedIdentity()?.role?.grade ?? 0
    const localCenter = getItemFromLocalStorage('localCenter')

    // multi-search on Profiles menu
    if (params.filter.multi_search) {
      let query = supabaseClient
        .from(resource)
        .select('*, role:roles(*)')
        .order('created_at', { ascending: false })
        .or(`first_name.ilike.%${params.filter.multi_search}%,last_name.ilike.%${params.filter.multi_search}%,email.ilike.%${params.filter.multi_search}%,phone.ilike.%${params.filter.multi_search}%`)

      if (localUserGrade && Number(localUserGrade) < 100) {
        query = query.neq('role_id', '597c594e-c095-4671-a2fa-bce91f2bf920') // no vision on admins

        if (localCenter && localCenter.id) {
          query = query.eq('center_id', localCenter.id)
        }
      }

      // Year filter: academic year runs Aug 1 to Jul 31
      if (params.filter.year) {
        const [startYear, endYear] = params.filter.year.split('/')
        query = query
          .gte('created_at', `${startYear}-08-01T00:00:00`)
          .lte('created_at', `${endYear}-07-31T23:59:59`)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching multisearch profiles:', error.message)
      }

      return { data: data ?? [], total: data?.length ?? 0 }
    }

    // Transforming filter object into filters array with operators
    const filter = params.filter
    const sorting = params.sort
    const operators = {
      '@gte': '>=',
      '@lte': '<=',
      '@neq': '!=',
      '@ilike': 'ilike',
      '@cs': 'cs',
      '@jsonb': 'jsonb',
    }

    // Transform filters object into filters array with operators (exclude year, handled separately)
    const filters = Object.keys(filter).filter(key => key !== 'year').map(key => {
      const matchedOperator = Object.keys(operators).find(op => key.endsWith(op));
      const operator = matchedOperator ? operators[matchedOperator] : '=';

      // Remove the operator part from the key
      const field = matchedOperator ? key.slice(0, -matchedOperator.length) : key;

      return { field, operator, value: filter[key] };
    })

    // Transforming query params and applying filters in Supabase query
    let query = supabaseClient
      .from(resource)
      .select('*', { count: 'exact' })

    if (localUserGrade && Number(localUserGrade) < 100) {
      query = query.neq('role_id', '597c594e-c095-4671-a2fa-bce91f2bf920') // no vision on admins

      if ( localCenter && safeUUID(localCenter.id) ) {
        query = query.eq('center_id', localCenter.id)
      }
    }

    // Year filter: academic year runs Aug 1 to Jul 31
    if (filter.year) {
      const [startYear, endYear] = filter.year.split('/')
      query = query
        .gte('created_at', `${startYear}-08-01T00:00:00`)
        .lte('created_at', `${endYear}-07-31T23:59:59`)
    }

    filters.forEach(filterObj => {
      // Strip accents for search_name filter (generated column is unaccented)
      if ( filterObj.field === 'search_name' && filterObj.operator === 'ilike' ) {
        filterObj.value = filterObj.value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      }

      if ( !Array.isArray(filterObj.value) ) {
        if (filterObj.operator === '=') {
          query = query.eq(filterObj.field, filterObj.value)
        } else if (filterObj.operator === '>=') {
          query = query.gte(filterObj.field, filterObj.value)
        } else if (filterObj.operator === '<=') {
          query = query.lte(filterObj.field, filterObj.value)
        } else if (filterObj.operator === '!=') {
          query = query.neq(filterObj.field, filterObj.value)
        } else if (filterObj.operator === 'ilike') {
          query = query.ilike(filterObj.field, `%${filterObj.value}%`)
        } else if (filterObj.operator === 'cs') {
          query = query.cs(filterObj.field, JSON.parse(filterObj.value))
        } else if (filterObj.operator === 'jsonb') {
          query.url.href = `${query.url.href}&or=(and(info_paie.cs.[${filterObj.value}]))`
        }
      } else {
        // Handle array values
        if (filterObj.operator === '!=') {
          filterObj.value.forEach(val => {
            query = query.neq(filterObj.field, val)
          })
        } else if (filterObj.operator === '=') {
          filterObj.value.forEach(val => {
            query = query.eq(filterObj.field, val)
          })
        }
      }
    })

    const { data, error, count } = await query
      .order(sorting?.field || 'created_at', { ascending: sorting?.order === 'ASC' })
      .range(
        (params.pagination.page - 1) * params.pagination.perPage,
        params.pagination.page * params.pagination.perPage - 1
      )

    if (error) {
      console.error('Error fetching profiles:', error.message)
      return { data: [], total: 0 }
    }

    return {
      data: data ?? [],
      total: count ?? 0,
    }
  },
}
